/**
 * Раздел-оверлей «Изобаты»: линии равных глубин с задаваемым шагом.
 *
 * Работает как «Волнение»: человек задаёт шаг — видит линии этого шага поверх
 * любого слоя (фидбек владельца 2026-08-07).
 *
 * Линии НЕ считаются на запрос: таблица `isobaths` уже содержит глубины от 0 до
 * −1000 м через 10 м и глубже — до −6000 м через 500 м (решение владельца
 * 11.09.2026), и шаг — это выбор кратных глубин из готового набора
 * (см. `src/api/cabinet_isobaths.py::depths_for_step`). Отсюда три честных
 * ограничения: мельче 10 м показать нечего, ниже 1000 м линии идут через 500 м
 * при любом шаге ползунка, глубже 6000 м их нет вовсе.
 *
 * Слой апвеллинга (`key='upwelling'`) живёт своей жизнью: у него свои пять
 * глубин и свои начертания — этот раздел его не трогает.
 */
import type { LineSwatch } from '../maps/overlaySwatch'
import { ISOBATH_STYLES } from './isobaths'

/** Мельче сетки таблицы шаг не опускается: контуров между уровнями не считали. */
export const ISOBATH_STEP_MIN = 10
export const ISOBATH_STEP_MAX = 500
/** Шаг ползунка — та же сетка 10 м: промежуточные значения ничего не дадут. */
export const ISOBATH_STEP_SLIDER = 10
/** 200 м — кромка шельфа: рабочий масштаб разговора о свалах глубин. */
export const ISOBATH_STEP_DEFAULT = 200

/** Глубже этого линий в таблице нет — интерфейс говорит об этом вслух. */
export const ISOBATH_DEEPEST_M = 6000

/** Кромка шельфа: до неё таблица идёт через 10 м, ниже — своей сеткой. */
export const ISOBATH_SHELF_M = 1000
/** Сетка таблицы ниже шельфа. Шаг ползунка (до 500 м) её не прореживает, и
 *  подпись обязана сказать, что ниже 1000 м разрыв другой, — иначе «шаг 50 м»
 *  над абиссалью читался бы как обещание, которого данные не держат. Та же
 *  пара чисел — в `cabinet_isobaths.py` и `build_isobaths.py::default_levels`. */
export const ISOBATH_DEEP_STEP_M = 500

/** Ключ слоя раздела. Отдельный от 'upwelling' — иначе разделы затирали бы
 *  линии друг друга при одновременном включении. */
export const ISOBATH_TOOL_KEY = 'tool'

/** Образец цвета для заголовка раздела: 200 м — та самая кромка шельфа. */
export const ISOBATH_SWATCH: LineSwatch = {
  kind: 'line',
  line: ISOBATH_STYLES[-200].color,
  casing: '#ffffff',
}

/** Лестница «зум → самый мелкий допустимый шаг», м (нижняя граница зума).
 *
 *  Цена кадра зависит не только от шага, но и от того, сколько в нём воды:
 *  замер прода 12.09.2026 — весь Дальний Восток шагом 10 м это 101 линия и
 *  3,34 млн вершин до упрощения против 229 тыс. у шага 200 м; кадр 2°×1°
 *  шагом 10 м — 159 тыс. Бюджет 230 тыс. вершин (столько раздел платит на
 *  своём дефолте и на предельном зуме), ступень — самый мелкий шаг, в него
 *  укладывающийся.
 *
 *  Копия таблицы живёт на сервере (`src/api/cabinet_isobaths.py::STEP_BY_ZOOM`)
 *  и он же авторитет: ползунок, обещающий шаг, которого ручка не отдаст, врал
 *  бы. Совпадение держит `tests/test_isobath_ladder_guard.py`. */
export const ISOBATH_STEP_BY_ZOOM: ReadonlyArray<readonly [number, number]> = [
  [10, 10], [9, 20], [7, 50], [6, 100], [0, 200],
]

/** Самый мелкий шаг, который на этом зуме имеет смысл просить. */
export function minIsobathStepForZoom(zoom: number): number {
  for (const [edge, step] of ISOBATH_STEP_BY_ZOOM) {
    if (zoom >= edge) return step
  }
  return ISOBATH_STEP_BY_ZOOM[ISOBATH_STEP_BY_ZOOM.length - 1][1]
}

/** Шаг, которым раздел рисует на самом деле.
 *
 *  Выбор человека только ГРУБЕЕТ: 500 м на предельном зуме остаются 500 м —
 *  лестница держит стоимость, а не назначает густоту. Зум неизвестен (карта
 *  ещё не сказала) — не трогаем вовсе: прижать «на всякий случай» значило бы
 *  показать не тот шаг на рабочем зуме, а зум приедет первым же moveend. */
export function effectiveIsobathStep(step: number, zoom: number | null): number {
  if (zoom == null) return step
  return Math.max(step, minIsobathStepForZoom(zoom))
}

export function isobathStepLabel(step: number): string {
  return String(Math.round(step))
}

/** Шаг из ссылки. Мусор и выход за диапазон → дефолт (пустой слой человек
 *  прочитал бы как «изобат здесь нет»). */
export function parseIsobathStep(raw: string | null): number {
  const n = raw == null || raw === '' ? NaN : Number(raw)
  if (!Number.isFinite(n) || n < ISOBATH_STEP_MIN || n > ISOBATH_STEP_MAX) {
    return ISOBATH_STEP_DEFAULT
  }
  return n
}
